import type {
  LalKitabKundliResponse,
  PlanetEffect,
  ActiveRemedy,
  DetectedDebt,
} from "@/lib/api/lal-kitab";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { OVERALL_LABEL, OVERALL_COLOR } from "./_constants";

function EffectBadge({ effect }: { effect: "good" | "bad" }) {
  return (
    <span
      className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{
        background: effect === "good" ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
        color: effect === "good" ? "var(--ish-success, #16a34a)" : "var(--ish-error, #dc2626)",
      }}
    >
      {effect === "good" ? "Comfortable" : "Needs Upay"}
    </span>
  );
}

export function PlanetEffectsTable({ effects }: { effects: PlanetEffect[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--ish-border)" }}>
            <th
              className="text-left py-2 pr-4 font-semibold"
              style={{ color: "var(--ish-text-muted)" }}
            >
              Planet
            </th>
            <th
              className="text-center py-2 pr-4 font-semibold"
              style={{ color: "var(--ish-text-muted)" }}
            >
              House
            </th>
            <th className="text-left py-2 font-semibold" style={{ color: "var(--ish-text-muted)" }}>
              Effect
            </th>
          </tr>
        </thead>
        <tbody>
          {effects.map((e) => (
            <tr
              key={e.planet}
              style={{ borderBottom: "1px solid var(--ish-border-faint, #f0ebe3)" }}
            >
              <td className="py-2 pr-4" style={{ color: "var(--ish-dark)" }}>
                <span className="font-medium">{e.planet_display_en}</span>
                <span className="ml-2 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                  {e.planet_display_hi}
                </span>
              </td>
              <td
                className="py-2 pr-4 text-center font-semibold"
                style={{ color: "var(--ish-accent)" }}
              >
                {e.house}
              </td>
              <td className="py-2">
                <EffectBadge effect={e.effect as "good" | "bad"} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RemedyCard({ remedy }: { remedy: ActiveRemedy }) {
  return (
    <div className="card-sacred p-4">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <p className="text-[15px] font-semibold" style={{ color: "var(--ish-dark)" }}>
          {remedy.planet_display_en}
        </p>
        <span className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
          House {remedy.house} · {remedy.day}
        </span>
      </div>
      {remedy.remedy_summary && (
        <p className="text-[13px] mb-2" style={{ color: "var(--ish-text-muted)" }}>
          {remedy.remedy_summary}
        </p>
      )}
      {remedy.donate.length > 0 && (
        <p className="text-[12px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--ish-accent)" }}>
            Donate:{" "}
          </span>
          {remedy.donate.join(", ")}
        </p>
      )}
      {remedy.feed && (
        <p className="text-[12px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--ish-accent)" }}>
            Feed:{" "}
          </span>
          {remedy.feed}
        </p>
      )}
      {remedy.gemstone && (
        <p className="text-[12px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--ish-accent)" }}>
            Gemstone:{" "}
          </span>
          {remedy.gemstone}
        </p>
      )}
    </div>
  );
}

export function DebtCard({ debt }: { debt: DetectedDebt }) {
  return (
    <div className="card-sacred p-4 border-l-4" style={{ borderLeftColor: "var(--ish-accent)" }}>
      <p className="text-[14px] font-semibold mb-1" style={{ color: "var(--ish-dark)" }}>
        {debt.name}
      </p>
      <p className="text-[12px] mb-2" style={{ color: "var(--ish-text-faint)" }}>
        {debt.planet_display_en} in House {debt.house}
      </p>
      <p className="text-[13px]" style={{ color: "var(--ish-text-muted)" }}>
        <span className="font-semibold">Remedy: </span>
        {debt.remedy}
      </p>
    </div>
  );
}

export function ResultPanel({ result }: { result: LalKitabKundliResponse }) {
  const overall = result.summary.overall;
  return (
    <div className="space-y-8">
      {/* Summary bar */}
      <div className="card-sacred p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p
              className="text-[11px] uppercase tracking-wide font-semibold mb-1"
              style={{ color: "var(--ish-text-faint)" }}
            >
              Overall Assessment
            </p>
            <p
              className="text-[20px] font-bold"
              style={{ color: OVERALL_COLOR[overall] ?? "var(--ish-dark)" }}
            >
              {OVERALL_LABEL[overall] ?? overall}
            </p>
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <p className="text-[24px] font-bold" style={{ color: "var(--ish-success, #16a34a)" }}>
                {result.summary.good_placements}
              </p>
              <p className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                Good
              </p>
            </div>
            <div>
              <p className="text-[24px] font-bold" style={{ color: "var(--ish-error, #dc2626)" }}>
                {result.summary.bad_placements}
              </p>
              <p className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                Need Upay
              </p>
            </div>
            <div>
              <p className="text-[24px] font-bold" style={{ color: "var(--ish-accent)" }}>
                {result.summary.total_remedies}
              </p>
              <p className="text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                Active Upay
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Planet positions table */}
      <div>
        <h2
          className="text-[18px] font-bold mb-3"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          Planet Placements (Lal Kitab)
        </h2>
        <div className="card-sacred p-4">
          <PlanetEffectsTable effects={result.planet_effects} />
        </div>
      </div>

      {/* Active remedies */}
      {result.active_remedies.length > 0 && (
        <div>
          <h2
            className="text-[18px] font-bold mb-3"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            Active Upay (Remedies)
          </h2>
          <p className="text-[13px] mb-4" style={{ color: "var(--ish-text-muted)" }}>
            These remedies apply to planets placed in houses that Lal Kitab marks as uncomfortable
            for them. Performing these upay regularly — especially on the specified day — is the
            core Lal Kitab prescription.
          </p>
          <div className="space-y-3">
            {result.active_remedies.map((r) => (
              <RemedyCard key={r.planet} remedy={r} />
            ))}
          </div>
        </div>
      )}

      {/* Ancestral debts */}
      {result.debts.length > 0 && (
        <div>
          <h2
            className="text-[18px] font-bold mb-3"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            Ancestral Debts (Rin / ऋण)
          </h2>
          <p className="text-[13px] mb-4" style={{ color: "var(--ish-text-muted)" }}>
            Lal Kitab identifies specific planet-house combinations as indicators of karmic debts —
            Pitru Rin, Matru Rin, Guru Rin, and others. Clearing these through the prescribed upay
            is considered essential for removing blockages in career, family, and health.
          </p>
          <div className="space-y-3">
            {result.debts.map((d, i) => (
              <DebtCard key={i} debt={d} />
            ))}
          </div>
        </div>
      )}

      <CalculatorCrossLinks currentPage="lal-kitab-kundli" />
    </div>
  );
}
